/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: ProfilePage.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-PROFILE
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProfilePage - Frontend UI module: ProfilePage.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракт компонента, блоки/логи загрузки профиля, END_METHOD.
 * END_CHANGE_SUMMARY
 */

import { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { getUserInfo } from '../api/userApi';
import { useAuth } from '../context/AuthContext';
import User from '../models/User';

/* START_METHOD_ProfilePage */
/* START_CONTRACT: ProfilePage
 * PURPOSE: Отобразить профиль текущего пользователя, загрузив данные при монтировании.
 * INPUTS: {} (использует AuthContext).
 * OUTPUTS: { JSX.Element - карточка профиля / индикатор / ошибка }.
 * SIDE_EFFECTS: HTTP getUserInfo; обновление AuthContext (setUser).
 * LINKS: M-FE-PROFILE, M-FE-AUTH
 * END_CONTRACT: ProfilePage
 */
export default function ProfilePage() {
  const { setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    async function fetchProfile() {
      try {
        const { data } = await getUserInfo(controller.signal);
        const user = User.fromResponse(data);
        /* START_BLOCK_LOAD_OK */
        setProfile(user);
        setUser(user);
        /* END_BLOCK_LOAD_OK */
      } catch (err) {
        /* START_BLOCK_LOAD_FAIL */
        if (controller.signal.aborted) return;
        console.warn('[ProfilePage][fetchProfile][LOAD_FAIL] profile load failed', {
          status: err.response?.status,
        });
        setError(
          err.response?.data?.message || 'Ошибка загрузки профиля',
        );
        /* END_BLOCK_LOAD_FAIL */
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchProfile();

    return () => {
      controller.abort();
    };
  }, [setUser]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!profile) {
    return null;
  }

  return (
    <Card sx={{ width: '100%', maxWidth: 600 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Профиль
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Email:</strong> {profile.email}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Статус:</strong> {profile.status}
        </Typography>
        <Typography variant="body1">
          <strong>Дата регистрации:</strong> {profile.registrationDate}
        </Typography>
      </CardContent>
    </Card>
  );
}
/* END_METHOD_ProfilePage */
